/**
 * CEX integration saga
 */
import { takeLatest, put, call, select } from 'redux-saga/effects';
import { ICexOrder } from '@kattana-io/common';
import {
  GET_CEX_HISTORY_ORDERS,
  GET_CEX_OPEN_ORDERS,
  GET_CEX_ORDERS,
  CANCEL_CEX_ORDER,
  actionGetHistoryOrders,
  actionGetOpenOrders,
  actionGetHistoryOrdersDone,
  actionGetOpenOrdersDone,
  actionGetOrdersFail,
  actionCancelCexOrderDone,
  actionCancelCexOrderFail,
} from '../actions/cex_orders';
import { authTokenSelector } from '../selectors/auth';
import { IStore } from '../reducers';
import { ECEXOrdersTab } from '../reducers/cex_orders';
import { actionCreateNotification } from '../actions/notifications';
import { LOG_OUT } from '../actions/auth';
import { CexAPI } from '../store/createStore';

function* onGetHistoryOrders(): any {
  const { base, quote } = yield select((state: IStore) => state.current);
  const exchange = yield select(
    (state: IStore) => state.cex_trading.currentExchange
  );
  const token = yield select(authTokenSelector);
  const resp = yield call(
    CexAPI.historyOrders,
    token,
    exchange,
    `${base}/${quote}`
  );
  if (resp) {
    const parsedOrders = resp.orders?.map((order: ICexOrder) => ({
      ...order.create,
      ...order.state,
    }));
    yield put(actionGetHistoryOrdersDone(parsedOrders || []));
  } else {
    yield put(actionGetOrdersFail());
  }
}

function* onGetOpenOrders(): any {
  const { base, quote } = yield select((state: IStore) => state.current);
  const exchange = yield select(
    (state: IStore) => state.cex_trading.currentExchange
  );
  const token = yield select(authTokenSelector);
  const resp = yield call(
    CexAPI.openOrders,
    token,
    exchange,
    `${base}/${quote}`
  );
  if (resp) {
    const parsedOrders = resp.orders?.map((order: ICexOrder) => ({
      ...order.create,
      ...order.state,
    }));
    yield put(actionGetOpenOrdersDone(parsedOrders || []));
  } else {
    yield put(actionGetOrdersFail());
  }
}

function* onGetOrders(): any {
  const tab = yield select((state: IStore) => state.cex_orders.tab);
  if (tab === ECEXOrdersTab.history) {
    yield put(actionGetHistoryOrders());
  } else if (tab === ECEXOrdersTab.open) {
    yield put(actionGetOpenOrders());
  }
}

function* onCancelOrder(action: any): any {
  const id = action.payload;
  const { base, quote } = yield select((state: IStore) => state.current);
  const exchange = yield select(
    (state: IStore) => state.cex_trading.currentExchange
  );
  const token = yield select(authTokenSelector);
  const resp = yield call(
    CexAPI.cancelOrder,
    token,
    exchange,
    `${base}/${quote}`,
    id
  );
  if (resp.status) {
    yield put(actionCancelCexOrderDone(id));
    yield put(
      actionCreateNotification({ kind: 'info', message: 'Order cancelled' })
    );
  } else {
    if (resp.error) {
      yield put(actionCancelCexOrderFail());
      yield put(
        actionCreateNotification({ kind: 'error', message: resp.error })
      );
    }
  }
}

function* onLogOut(): any {
  try {
    yield put(actionGetOrdersFail());
  } catch (error) {
    console.error(error);
  }
}

export default function* cexSaga() {
  yield takeLatest(GET_CEX_HISTORY_ORDERS, onGetHistoryOrders);
  yield takeLatest(GET_CEX_OPEN_ORDERS, onGetOpenOrders);
  yield takeLatest(GET_CEX_ORDERS, onGetOrders);
  yield takeLatest(CANCEL_CEX_ORDER, onCancelOrder);
  yield takeLatest(LOG_OUT, onLogOut);
}
